import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useApiClient } from "../../api/ApiContext";
import { ApiError } from "../../api/errors";
import { queryKeys } from "../../api/queryKeys";
import { correctionListSchema, correctionPreviewSchema, correctionSchema } from "../../api/schemas";
import type { Correction, CorrectionPreview, CorrectionRequestBody } from "../../api/schemas";

/**
 * Loads the visible correction requests (own and, for managers, the team's).
 *
 * @returns The query result.
 */
export function useCorrections(): UseQueryResult<Correction[]> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: queryKeys.corrections("all"),
    queryFn: ({ signal }) => client.get("/api/v1/corrections", correctionListSchema, { signal }),
  });

  return query;
}

/**
 * Previews the effect of a correction without storing it.
 *
 * @returns The mutation.
 */
export function usePreviewCorrection(): UseMutationResult<
  CorrectionPreview,
  Error,
  CorrectionRequestBody
> {
  const client = useApiClient();
  const mutation = useMutation({
    mutationFn: (body: CorrectionRequestBody) =>
      client.post("/api/v1/corrections:preview", body, correctionPreviewSchema),
  });

  return mutation;
}

/**
 * Invalidates everything a correction or decision can change.
 *
 * @returns The invalidation function.
 */
function useInvalidateRecords(): () => Promise<void> {
  const queryClient = useQueryClient();
  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.allCorrections }),
      queryClient.invalidateQueries({ queryKey: ["me"] }),
      queryClient.invalidateQueries({ queryKey: queryKeys.allViolations }),
    ]);
  };

  return invalidate;
}

/**
 * Reloads the requests after a decision or withdrawal that came too late (TT-1009, F-61): another
 * request decided or withdrew the request meanwhile, so the list shows its current status.
 *
 * @param invalidate - The invalidation of the records.
 * @returns The error handler of the mutation.
 */
function reloadWhenDecidedMeanwhile(invalidate: () => Promise<void>): (error: Error) => void {
  const onError = (error: Error): void => {
    if (error instanceof ApiError && error.code === "TT-1009") {
      void invalidate();
    }
  };

  return onError;
}

/**
 * Submits a correction request.
 *
 * @returns The mutation.
 */
export function useSubmitCorrection(): UseMutationResult<Correction, Error, CorrectionRequestBody> {
  const client = useApiClient();
  const invalidate = useInvalidateRecords();
  const mutation = useMutation({
    mutationFn: (body: CorrectionRequestBody) =>
      client.post("/api/v1/corrections", body, correctionSchema),
    onSuccess: invalidate,
  });

  return mutation;
}

/** Input of a decision. */
export interface DecisionInput {
  readonly id: string;
  readonly approve: boolean;
  readonly note: string | null;
}

/**
 * Approves or rejects a correction request (four-eyes principle, ADR-0018).
 *
 * @returns The mutation.
 */
export function useDecideCorrection(): UseMutationResult<Correction, Error, DecisionInput> {
  const client = useApiClient();
  const invalidate = useInvalidateRecords();
  const mutation = useMutation({
    mutationFn: (input: DecisionInput) =>
      client.post(
        `/api/v1/corrections/${encodeURIComponent(input.id)}:${input.approve ? "approve" : "reject"}`,
        { note: input.note },
        correctionSchema,
      ),
    onSuccess: invalidate,
    onError: reloadWhenDecidedMeanwhile(invalidate),
  });

  return mutation;
}

/**
 * Withdraws an own open correction request before it is decided (F-61): the server keeps it as
 * withdrawn, audits the step and applies nothing. Online only, like submitting a request; without a
 * connection the error says so and the request stays open.
 *
 * @returns The mutation.
 */
export function useWithdrawCorrection(): UseMutationResult<Correction, Error, string> {
  const client = useApiClient();
  const invalidate = useInvalidateRecords();
  const mutation = useMutation({
    mutationFn: (id: string) =>
      client.post(`/api/v1/corrections/${encodeURIComponent(id)}:withdraw`, {}, correctionSchema),
    onSuccess: invalidate,
    onError: reloadWhenDecidedMeanwhile(invalidate),
  });

  return mutation;
}
